import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Zap, Check, Clock, ArrowRight, Lock, Rocket, Building2, Network, Info } from "lucide-react";
import { B2B_COMPETITORS, B2B_COMPARISON_TABLE, SACVPN_B2B_ADVANTAGES } from "../data/b2bCompetitors.js";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const comparisonData = [
  {
    feature: "Protocol",
    sacvpn: "WireGuard",
    competitors: "OpenVPN/IKEv2",
    advantage: "Modern, lightweight tunnel"
  },
  {
    feature: "Overhead",
    sacvpn: "Light footprint",
    competitors: "Heavier footprint",
    advantage: "Less processing per connection"
  },
  {
    feature: "Encryption",
    sacvpn: "ChaCha20-Poly1305",
    competitors: "AES-256",
    advantage: "Modern cipher, efficient on mobile"
  },
  {
    feature: "Code base",
    sacvpn: "~4,000 lines",
    competitors: "~400,000+ lines",
    advantage: "Easier to audit, smaller attack surface"
  },
  {
    feature: "Logging policy",
    sacvpn: "No activity logs",
    competitors: "Varies",
    advantage: "We never log your browsing activity"
  },
  {
    feature: "Devices",
    sacvpn: "Unlimited (personal)",
    competitors: "Often capped",
    advantage: "Protect the whole household"
  },
  {
    feature: "Business features",
    sacvpn: "Included",
    competitors: "Extra cost",
    advantage: "Team management at no extra cost"
  }
];

const competitors = [
  {
    name: "NordVPN",
    price: "$12.99/mo",
    protocol: "OpenVPN/IKEv2",
    logs: "No logs (audited)"
  },
  {
    name: "ExpressVPN",
    price: "$12.95/mo",
    protocol: "Lightway (proprietary)",
    logs: "No activity logs"
  },
  {
    name: "Surfshark",
    price: "$12.95/mo",
    protocol: "OpenVPN/WireGuard",
    logs: "No logs"
  },
  {
    name: "CyberGhost",
    price: "$12.99/mo",
    protocol: "OpenVPN/WireGuard",
    logs: "No logs (audited)"
  }
];

const whyWireguard = [
  {
    title: "Lean by design",
    description: "WireGuard is built from the ground up to be minimal. It uses modern cryptography and a small codebase.",
    icon: Rocket
  },
  {
    title: "Auditable",
    description: "At roughly 4,000 lines of code versus 400,000+ for OpenVPN, WireGuard can be fully audited by security researchers.",
    icon: Shield
  },
  {
    title: "Modern cryptography",
    description: "ChaCha20 for encryption, Curve25519 for key exchange, and BLAKE2s for hashing.",
    icon: Lock
  },
  {
    title: "Quick reconnects",
    description: "WireGuard handles roaming well, which suits mobile devices that switch between Wi-Fi and cellular.",
    icon: Zap
  }
];

const sacvpnAdvantages = [
  "WireGuard-only provider (no legacy protocols)",
  "Business and personal plans with the same features",
  "14-day free trial, no credit card",
  "US-based company",
  "US-based email support",
  "Simple, transparent pricing"
];

export default function Compare() {
  return (
    <>
      <Helmet>
        <title>SACVPN vs Other VPNs & Business ZTNA | VPN Comparison</title>
        <meta
          name="description"
          content="Compare SACVPN with NordVPN, ExpressVPN, Surfshark, and more, plus business VPN and Zero Trust platforms like NordLayer, GoodAccess, Twingate, and Cloudflare Access. See how a WireGuard VPN compares on protocol, pricing, and seat minimums."
        />
        <meta
          name="keywords"
          content="VPN comparison, SACVPN vs NordVPN, SACVPN vs ExpressVPN, SACVPN vs NordLayer, SACVPN vs GoodAccess, SACVPN vs Twingate, SACVPN vs Cloudflare Access, business VPN comparison, WireGuard vs OpenVPN, ZTNA vs VPN"
        />
        <link rel="canonical" href="https://www.sacvpn.com/compare" />
        <meta property="og:title" content="SACVPN vs Other VPNs & Business ZTNA | VPN Comparison" />
        <meta property="og:description" content="Compare SACVPN with NordVPN, ExpressVPN, Surfshark, and more, plus business VPN and Zero Trust platforms like NordLayer, GoodAccess, Twingate, and Cloudflare Access. See how a WireGuard VPN compares on protocol, pricing, and seat minimums." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "VPN Comparison - SACVPN vs Competitors",
            "description": "Detailed comparison of SACVPN with other VPN providers and business Zero Trust platforms",
            "publisher": {
              "@type": "Organization",
              "name": "SACVPN"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
              Honest VPN comparison
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
            >
              How SACVPN compares to{" "}
              <span className="text-brand-400">other VPNs</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed"
            >
              We believe in transparency. Here's an honest look at how SACVPN stacks up against the competition, and why WireGuard matters.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Quick Comparison Table */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-12"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">The details</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Feature-by-feature comparison
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="overflow-x-auto rounded-xl border border-white/[0.08]"
          >
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-4 px-5 font-semibold text-white">Feature</th>
                  <th className="text-center py-4 px-5 font-semibold text-brand-400 bg-brand-500/[0.06]">SACVPN</th>
                  <th className="text-center py-4 px-5 font-semibold text-gray-400">Most VPNs</th>
                  <th className="text-left py-4 px-5 font-semibold text-gray-400 hidden lg:table-cell">Why it matters</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b border-white/[0.06] last:border-0">
                    <td className="py-4 px-5 font-medium text-white">{row.feature}</td>
                    <td className="py-4 px-5 text-center bg-brand-500/[0.04]">
                      <span className="inline-flex items-center gap-2 text-gray-200 font-medium">
                        <Check className="w-4 h-4 text-brand-400" />
                        {row.sacvpn}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-center text-gray-400">{row.competitors}</td>
                    <td className="py-4 px-5 text-gray-500 text-sm hidden lg:table-cell">{row.advantage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Why WireGuard Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">The protocol</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Why we build on WireGuard
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Many VPNs still lean on OpenVPN, first released in 2001. We build on WireGuard, a modern protocol with a small, auditable codebase.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {whyWireguard.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Price Comparison */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-12"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Pricing</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Price comparison
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Monthly pricing. Competitor prices shown are their advertised monthly rates.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-5 gap-6"
          >
            {/* SACVPN Card - Highlighted */}
            <motion.div
              variants={fadeUp}
              className="rounded-xl border border-brand-500/30 bg-brand-500/[0.06] p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg text-white">SACVPN</h3>
                <span className="px-2 py-1 bg-brand-500/20 text-brand-300 rounded-md text-xs font-mono uppercase tracking-[0.12em]">Recommended</span>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-display font-bold text-white tracking-tight">$9.99</span>
                <span className="text-gray-500">/mo</span>
              </div>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-400" />
                  WireGuard only
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-400" />
                  14-day free trial
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-400" />
                  No credit card
                </li>
              </ul>
            </motion.div>

            {/* Competitor Cards */}
            {competitors.map((comp, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6"
              >
                <h3 className="font-semibold text-lg text-white mb-4">{comp.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-display font-bold text-gray-300 tracking-tight">{comp.price}</span>
                </div>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>{comp.protocol}</li>
                  <li>{comp.logs}</li>
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* B2B / Business VPN & ZTNA Comparison */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-12"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">For teams and businesses</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              How SACVPN compares to business VPN & Zero Trust platforms
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              If you're evaluating SACVPN for a team, you're probably also looking at NordLayer, GoodAccess, Twingate, or
              Cloudflare Access. Here's an honest breakdown, including where those tools do things SACVPN doesn't try to do.
            </p>
          </motion.div>

          {/* Feature comparison table */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="overflow-x-auto rounded-xl border border-white/[0.08] mb-14"
          >
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-4 px-5 font-semibold text-white">Feature</th>
                  <th className="text-center py-4 px-5 font-semibold text-brand-400 bg-brand-500/[0.06]">SACVPN</th>
                  <th className="text-center py-4 px-5 font-semibold text-gray-400">NordLayer / GoodAccess / Twingate / Cloudflare</th>
                  <th className="text-left py-4 px-5 font-semibold text-gray-400 hidden lg:table-cell">Why it matters</th>
                </tr>
              </thead>
              <tbody>
                {B2B_COMPARISON_TABLE.map((row, i) => (
                  <tr key={i} className="border-b border-white/[0.06] last:border-0">
                    <td className="py-4 px-5 font-medium text-white">{row.feature}</td>
                    <td className="py-4 px-5 text-center bg-brand-500/[0.04]">
                      <span className="inline-flex items-center gap-2 text-gray-200 font-medium">
                        <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                        {row.sacvpn}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-center text-gray-400 text-sm">{row.competitors}</td>
                    <td className="py-4 px-5 text-gray-500 text-sm hidden lg:table-cell">{row.advantage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* VPN vs ZTNA explainer */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-xl border border-brand-500/20 bg-brand-500/[0.04] p-6 md:p-8 mb-14 flex gap-4"
          >
            <Info className="w-5 h-5 text-brand-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-white mb-2">VPN vs. Zero Trust (ZTNA): different tools, different jobs</h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                Twingate and Cloudflare Access aren't VPNs, they're Zero Trust Network Access (ZTNA) platforms. Instead of
                joining your network the way a VPN does, they broker access to individual apps and resources one at a time,
                usually through per-subnet connectors. That's a real, legitimate approach, and for teams that only need
                secure access to a handful of SaaS apps, it can be simpler and more locked-down than a full-tunnel VPN.
                SACVPN takes a different path: a full-tunnel WireGuard VPN that puts you on the network like you're in the
                office, which matters if your team needs remote desktops, printers, shared drives, or legacy on-prem systems
                that a resource-by-resource access broker isn't built to reach. Neither approach is strictly "better" - pick
                the one that matches what your team actually needs to reach.
              </p>
            </div>
          </motion.div>

          {/* Business plan cards */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-5 gap-6"
          >
            {/* SACVPN Business Card - Highlighted */}
            <motion.div
              variants={fadeUp}
              className="rounded-xl border border-brand-500/30 bg-brand-500/[0.06] p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-brand-400" />
                <span className="px-2 py-1 bg-brand-500/20 text-brand-300 rounded-md text-xs font-mono uppercase tracking-[0.12em]">SACVPN Business</span>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-display font-bold text-white tracking-tight">$100</span>
                <span className="text-gray-500">/mo</span>
              </div>
              <p className="text-sm text-gray-400 mb-4">Up to 10 devices. Larger tiers scale from there.</p>
              <ul className="space-y-2 text-sm text-gray-400">
                {SACVPN_B2B_ADVANTAGES.map((advantage, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-400 flex-shrink-0 mt-0.5" />
                    {advantage}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* B2B Competitor Cards */}
            {B2B_COMPETITORS.map((comp, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Network className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500">{comp.category}</span>
                </div>
                <h3 className="font-semibold text-lg text-white mb-2">{comp.name}</h3>
                <div className="mb-1">
                  <span className="text-2xl font-display font-bold text-gray-300 tracking-tight">{comp.price}</span>
                </div>
                <p className="text-xs text-gray-500 mb-4">{comp.priceNote}</p>
                <ul className="space-y-2 text-sm text-gray-400 mb-4">
                  <li>{comp.minimum}</li>
                  <li>{comp.protocol}</li>
                </ul>
                <p className="text-xs text-gray-500 border-t border-white/[0.06] pt-3 leading-relaxed">{comp.note}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-6 text-xs text-gray-500 max-w-3xl"
          >
            Competitor pricing above reflects publicly published rates as of mid-2026. This market reprices and
            restructures tiers often, so confirm current numbers on each vendor's own pricing page before making a
            purchase decision.
          </motion.p>
        </div>
      </section>

      {/* Why Choose SACVPN */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                The difference
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Focused on doing one thing well
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                We're not trying to be everything to everyone. We focus on a private, secure VPN built entirely around WireGuard.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="space-y-3"
              >
                {sacvpnAdvantages.map((advantage, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0" />
                    <span className="text-gray-400">{advantage}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-900 p-8"
            >
              <div className="mb-8">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-3">Try before you buy</p>
                <h3 className="text-2xl font-display font-bold text-white tracking-tight mb-2">14-day free trial</h3>
                <p className="text-gray-400">No credit card required.</p>
              </div>

              <div className="space-y-1 mb-8">
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <span className="text-gray-400">Personal plan</span>
                  <span className="text-white font-semibold">$9.99/month</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <span className="text-gray-400">Business plan</span>
                  <span className="text-white font-semibold">$7/user/month</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-400">Enterprise</span>
                  <span className="text-white font-semibold">Contact us</span>
                </div>
              </div>

              <Link
                to="/pricing"
                className="block w-full text-center px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                View full pricing
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Common questions</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Questions people ask
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            {[
              {
                q: "How is WireGuard different from OpenVPN?",
                a: "WireGuard is a newer protocol with a much smaller codebase (roughly 4,000 lines versus OpenVPN's 400,000+). That makes it easier to audit and gives it a lighter footprint per connection."
              },
              {
                q: "Why don't other VPNs use WireGuard?",
                a: "Many are starting to offer it as an option, but their infrastructure was built around OpenVPN. SACVPN was built from day one around WireGuard, so every part of our system is designed for it."
              },
              {
                q: "Can I try SACVPN without a credit card?",
                a: "Yes. We offer a full 14-day trial with no credit card required. Many competitors either require payment upfront or only offer a money-back guarantee."
              },
              {
                q: "Is SACVPN good for streaming?",
                a: "Yes. Our global node network helps you reach content in different regions and reduce ISP throttling. As always, streaming quality depends on your own connection."
              },
              {
                q: "Do I need 5 or more employees to buy SACVPN Business?",
                a: "No. Unlike NordLayer and GoodAccess's Essential plan, which both require a 5-seat minimum, SACVPN Business is priced by device capacity starting at 10 devices for $100/month. There's no headcount floor to qualify."
              },
              {
                q: "What's the difference between SACVPN and Zero Trust tools like Twingate or Cloudflare Access?",
                a: "Twingate and Cloudflare Access are Zero Trust Network Access (ZTNA) platforms, not VPNs. They broker access to individual apps and resources one at a time through per-subnet connectors, which works well if your team only needs secure access to a handful of SaaS tools. SACVPN is a full-tunnel WireGuard VPN, so it puts you on the network the way you would be in the office, which matters if you need remote desktops, printers, or legacy on-prem systems. Neither is strictly better; they solve different problems."
              },
              {
                q: "How does SACVPN compare to NordLayer or GoodAccess on pricing?",
                a: "NordLayer and GoodAccess both publish per-seat pricing with a 5-seat purchase minimum (GoodAccess Enterprise requires 50 seats), and their top tiers are contact-sales only. SACVPN publishes real prices for every tier, including business, with no seat minimum to buy in."
              }
            ].map((faq, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6"
              >
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Ready to see the difference?
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Try SACVPN free for 14 days. No credit card, no commitment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/download"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                View pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
